import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, RefreshCw, X, Plus, Wallet, TrendingUp, AlertTriangle } from 'lucide-react';
import { apiGetPortfolio, apiExecuteTrade, apiCloseTrade, apiGetTrades, apiGetPortfolioHistory, apiGetPortfolioHeat } from '../utils/backendApi';
import { computeSuggestedPositionSize, RISK_PER_TRADE_PCT } from '../utils/marketEngine';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
import CountUp from '../components/ui/CountUp';
import PriceRail from '../components/ui/PriceRail';
import { TERMS } from '../utils/terms';
import { getQuote, getBatchQuotes, getTechnicals, scoreTickerSignal } from '../utils/marketEngine';
import { getOptionContractQuote, getOptionQuoteByDetails, getSectorMap as polygonGetSectorMap } from '../utils/polygonService';
import { SECTOR_ETF_MAP, classifyClientSectorRanks } from '../utils/sectorRank';
import { useExchange } from '../context/ExchangeContext';
import { formatDateOnly } from '../utils/format';

const tooltipStyle = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12, color: '#e2e8f0' };
const displayTicker = t => t || '';

// ── Helpers ────────────────────────────────────────────────────────────────

// Estimate current value of a single open trade given live quote data.
// Returns { currentPrice, currentVal, unrealizedPnl, pnlPct }
//
// SHORT mechanics (paper trading):
//   Open:  cash -= qty * entryPrice  (you commit capital)
//   Close: cash += (committed capital) + realizedPnl
//   So unrealized P&L for SHORT = costBasis - currentVal  (profit when price falls)
//   Position value in portfolio  = costBasis + unrealizedPnl = 2*costBasis - currentVal
// liveOptionQuote: result from getOptionContractQuote (real Polygon data), or null → fall back to entry price
function calcPosition(trade, liveQuote, liveOptionQuote = null) {
  const qty        = parseFloat(trade.quantity);
  const entryPrice = parseFloat(trade.price);
  const costBasis  = parseFloat(trade.total_value);
  const isOption   = trade.asset_type === 'option';
  const isShort    = trade.direction === 'SHORT';
  const stockPrice = liveQuote?.price || 0;

  let currentPrice, currentVal, pricingSource;

  if (isOption) {
    // Trust the Polygon midpoint when it's a live bid/ask OR a session close.
    // When the only thing Polygon hands us is a last_trade.price that hasn't
    // refreshed in hours/days, fall back to entry price rather than show a
    // misleading P&L = $0 from echoing entry back.
    const STALE_MS  = 12 * 60 * 60 * 1000;
    const lastTrade = liveOptionQuote?.lastTradeAt;
    const usable    = liveOptionQuote?.midpointSource === 'live'
                   || liveOptionQuote?.midpointSource === 'session_close'
                   || (liveOptionQuote?.midpointSource === 'last_trade'
                       && lastTrade && (Date.now() - lastTrade) < STALE_MS);
    if (liveOptionQuote?.midpoint != null && usable) {
      currentPrice  = liveOptionQuote.midpoint;
      currentVal    = qty * currentPrice * 100;
      pricingSource = liveOptionQuote.isLive ? 'live' : 'polygon_closed';
    } else {
      currentPrice  = entryPrice;
      currentVal    = qty * currentPrice * 100;
      pricingSource = 'entry';
    }
  } else {
    currentPrice  = stockPrice || entryPrice;
    currentVal    = qty * currentPrice;
    pricingSource = stockPrice > 0 ? 'live' : 'entry';
  }

  // Options are always held long — direction is the market view, not the option side.
  const unrealizedPnl  = (isOption || !isShort) ? currentVal - costBasis : costBasis - currentVal;
  const pnlPct         = costBasis > 0 ? (unrealizedPnl / costBasis) * 100 : 0;
  const portfolioValue = isOption ? currentVal : (isShort ? costBasis + unrealizedPnl : currentVal);

  return {
    currentPrice, currentVal, unrealizedPnl, pnlPct, portfolioValue, costBasis,
    qty, entryPrice, isOption, isShort, stockPrice, pricingSource,
  };
}

// ── Signal health checker ──────────────────────────────────────────────────
// Re-scores the underlying stock using the EXACT same inputs as the signal
// generator: intraday changePct (vs day open, not prev close) + SPY as the
// index reference for relative-strength scoring.
// Option-specific exit rules layered on top of the underlying-signal check.
// Returns { status, reason } only when an option-level threshold trips —
// the SignalHealth widget then overrides the underlying-signal verdict.
//
// The pnlPct used here is the position's actual mark-to-market P&L (from
// calcPosition), which on Options Starter is the delta-adjusted estimate —
// so these rules respond to intraday underlying moves the moment they
// happen, not once a day at the close.
function evaluateOptionExitRules({ pnlPct, optionExpiry }) {
  // Stop-loss: a 50% premium drawdown is a near-universal trader cutoff —
  // below that, the convex math of buying premium turns hostile fast.
  if (pnlPct != null && pnlPct <= -50) {
    return { status: 'exit', reason: `Premium down ${Math.abs(pnlPct).toFixed(0)}% — stop-loss threshold hit` };
  }
  // Take-profit: doubled premium is a reasonable swing target on a debit
  // position; theta starts hurting hard past this point.
  if (pnlPct != null && pnlPct >= 100) {
    return { status: 'exit', reason: `Premium up ${pnlPct.toFixed(0)}% — take-profit threshold hit` };
  }
  // Theta crunch zone: <5 trading days to expiry, with no thesis-confirmation
  // gain, means gamma's about to dominate. Better to roll or close.
  if (optionExpiry) {
    const iso = String(optionExpiry).slice(0, 10);
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const expiryMs = Date.UTC(+m[1], +m[2] - 1, +m[3]);
      const daysLeft = (expiryMs - Date.now()) / 86_400_000;
      if (daysLeft < 5 && daysLeft >= 0 && (pnlPct == null || pnlPct < 20)) {
        return { status: 'exit', reason: `${Math.max(0, daysLeft).toFixed(1)}d to expiry — theta acceleration` };
      }
    }
  }
  return null;
}

function SignalHealth({ ticker, direction, entryPrice, currentPrice, optionContext }) {
  const [status, setStatus] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [currentSignalDir, setCurrentSignalDir] = useState(null);
  const [optionRule, setOptionRule] = useState(null);
  const [checking, setChecking] = useState(true);

  const check = useCallback(async (bypassCache = false) => {
    setChecking(true);
    try {
      // Sector ETFs + the position's sector are fetched alongside the usual
      // technicals/quote. Sector RS is the same data the background health
      // monitor reads, so the on-page verdict matches what the email job
      // would decide at the next 15-min tick.
      const etfTickers = Object.values(SECTOR_ETF_MAP);
      const [tech, quote, indexQ, sectorByTicker, etfQuotes] = await Promise.all([
        getTechnicals(ticker, { bypassCache }),
        getQuote(ticker, { bypassCache }),
        getQuote('SPY').catch(() => null),
        polygonGetSectorMap([ticker]).catch(() => ({})),
        getBatchQuotes(etfTickers).catch(() => ({})),
      ]);

      const hasData = tech?.rsi != null || tech?.macd != null || tech?.bollingerUpper != null;
      if (!hasData) { setStatus('unavailable'); setChecking(false); return; }

      const livePrice = currentPrice > 0 ? currentPrice : (quote?.price || 0);

      // Use intraday changePct (current price vs TODAY's session open) — the signal scanner
      // uses (close - open) / open from the grouped-daily bar for the same trading day.
      // When today's session open is unavailable (illiquid stock, pre-market, or Polygon
      // hasn't recorded it yet), default to 0 — neutral. Never use the day-over-day
      // changePct as a fallback: it includes the overnight gap and flips the direction
      // for any stock that gapped significantly from yesterday's close.
      const intradayChangePct = (quote?.open > 0 && livePrice > 0)
        ? (livePrice - quote.open) / quote.open * 100
        : 0;

      const indexChangePct = indexQ?.open > 0
        ? (indexQ.price - indexQ.open) / indexQ.open * 100
        : (indexQ?.changePct || 0);

      const q = { ...(quote || {}), price: livePrice, changePct: intradayChangePct };
      const scored = scoreTickerSignal(q, tech, { indexChangePct });
      const sigDir = scored.direction;

      const tradeDir = direction === 'BUY' ? 'LONG' : 'SHORT';
      let newStatus;
      if (sigDir === tradeDir && scored.score >= 4) {
        newStatus = 'hold';
      } else if (sigDir !== 'HOLD' && sigDir !== tradeDir && scored.score >= 4) {
        newStatus = 'exit';
      } else {
        newStatus = 'monitor';
      }

      // Phase A sector RS escalator — mirrors the server-side computeSignalHealth
      // logic. If the position's sector has rotated against the trade direction,
      // escalate HOLD → MONITOR. Sector rotation is macro context, not a thesis
      // break, so it never forces a HOLD/MONITOR straight to EXIT.
      const tradeSector = sectorByTicker[ticker] || null;
      const sectorRank = classifyClientSectorRanks(etfQuotes);
      let sectorEscalation = null;
      if (tradeSector && sectorRank.size > 0) {
        const sectorState = sectorRank.get(tradeSector);
        const sectorAgainst =
          (tradeDir === 'LONG'  && sectorState === 'lagging') ||
          (tradeDir === 'SHORT' && sectorState === 'leading');
        if (sectorAgainst) {
          sectorEscalation = `Sector ${tradeSector} ${sectorState} the tape (against ${tradeDir} thesis)`;
          if (newStatus === 'hold') newStatus = 'monitor';
        }
      }

      // Option-specific overrides: P&L stop/take-profit and theta-crunch
      // window. These run AFTER the underlying-signal verdict so a clear
      // technical signal still wins on borderline P&L, but a 50% drawdown
      // or 5-days-to-expiry triggers an EXIT regardless of what the chart
      // is doing.
      const opt = optionContext && optionContext.isOption
        ? evaluateOptionExitRules({ pnlPct: optionContext.pnlPct, optionExpiry: optionContext.optionExpiry })
        : null;
      if (opt) {
        newStatus = opt.status;
      }

      setCurrentSignalDir(sigDir);
      setReasons(sectorEscalation ? [sectorEscalation, ...scored.reasons] : scored.reasons);
      setOptionRule(opt);
      setStatus(newStatus);
    } catch {
      setStatus('unavailable');
    }
    setChecking(false);
  }, [ticker, direction, currentPrice, optionContext?.isOption, optionContext?.pnlPct, optionContext?.optionExpiry]);

  useEffect(() => { check(); }, [check]);

  if (checking) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', marginBottom: 14 }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#334155', display: 'inline-block', animation: 'pulse-glow 1.5s ease-in-out infinite' }} />
      <span style={{ fontSize: 11, color: '#475569' }}>Checking current conditions…</span>
    </div>
  );

  if (status === 'unavailable') return (
    <div style={{ padding: '8px 10px', borderRadius: 8, background: 'rgba(51,65,85,0.15)', border: '1px solid rgba(51,65,85,0.25)', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 10, color: '#475569' }}>Technicals unavailable — market may be closed or data loading</span>
      <button onClick={() => check(true)} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 10, fontFamily: 'inherit', padding: 0, flexShrink: 0 }}>↻ Retry</button>
    </div>
  );

  if (!status) return null;

  const cfg = {
    hold:    { bg: 'rgba(16,185,129,0.08)',  border: 'rgba(16,185,129,0.2)',  dot: '#10b981', color: '#10b981', label: 'HOLD',         text: 'Signal still supports this trade' },
    monitor: { bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.2)',  dot: '#f59e0b', color: '#f59e0b', label: 'MONITOR',       text: 'Conviction weakening — watch closely' },
    exit:    { bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.2)',   dot: '#ef4444', color: '#ef4444', label: 'CONSIDER EXIT', text: 'Signal reversed — conditions no longer support this trade' },
  }[status];

  // P&L projection vs entry
  const tradeDir = direction === 'BUY' ? 'LONG' : 'SHORT';
  let pnlNote = null;
  if (entryPrice > 0 && currentPrice > 0) {
    const movePct = ((currentPrice - entryPrice) / entryPrice * 100);
    const inProfit = tradeDir === 'LONG' ? movePct > 0 : movePct < 0;
    const absPct = Math.abs(movePct).toFixed(1);
    pnlNote = {
      text: inProfit ? `+${absPct}% from entry — on track` : `−${absPct}% from entry — moving against trade`,
      color: inProfit ? '#10b981' : '#ef4444',
    };
  }

  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, background: cfg.bg, border: `1px solid ${cfg.border}`, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot, display: 'inline-block', flexShrink: 0,
            boxShadow: status === 'exit' ? `0 0 6px ${cfg.dot}` : 'none',
          }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color, letterSpacing: '0.05em' }}>{cfg.label}</span>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {optionRule?.reason || cfg.text}
          </span>
          {currentSignalDir && currentSignalDir !== 'HOLD' && (
            <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, fontWeight: 600,
              background: currentSignalDir === 'LONG' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
              color: currentSignalDir === 'LONG' ? '#10b981' : '#ef4444',
            }}>Signal now: {currentSignalDir}</span>
          )}
        </div>
        <button onClick={() => check(true)} disabled={checking} style={{ background: 'none', border: 'none', color: '#475569', cursor: checking ? 'wait' : 'pointer', fontSize: 10, fontFamily: 'inherit', padding: 0, flexShrink: 0 }}>
          ↻ Recheck
        </button>
      </div>
      {pnlNote && (
        <div style={{ fontSize: 9, color: pnlNote.color, fontWeight: 600, marginBottom: 5 }}>{pnlNote.text}</div>
      )}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 4 }}>
        {reasons.map((r, i) => (
          <span key={i} style={{ fontSize: 9, padding: '2px 7px', borderRadius: 3, background: 'rgba(15,23,42,0.6)', color: '#64748b', border: '1px solid rgba(51,65,85,0.3)' }}>{r}</span>
        ))}
      </div>
    </div>
  );
}

// ── Position card ──────────────────────────────────────────────────────────

function PositionCard({ trade, liveQuotes, optionQuotes, cs, onClose, onViewChart, closing }) {
  const q          = liveQuotes[trade.ticker];
  const liveOptQ   = optionQuotes[trade.id] ?? null;
  const pos        = calcPosition(trade, q, liveOptQ);
  const up  = pos.unrealizedPnl >= 0;
  const dirGrad = up ? 'var(--grad-success)' : 'var(--grad-danger)';
  const priceChange    = pos.currentPrice - pos.entryPrice;
  const priceChangePct = pos.entryPrice > 0 ? (priceChange / pos.entryPrice) * 100 : 0;

  return (
    <div className="card animate-fade-in" style={{
      padding: '18px 20px', position: 'relative', overflow: 'hidden',
    }}>
      {/* Direction stripe — matches SignalCard visual language */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: dirGrad, opacity: 0.85,
      }} />

      {/* Header row */}
      <div className="position-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span className="mono" style={{
              fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', cursor: 'pointer',
              letterSpacing: '-0.3px', lineHeight: 1,
            }}
              onClick={() => onViewChart(trade.ticker)}>{displayTicker(trade.ticker)}</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              fontSize: 10, padding: '3px 9px', borderRadius: 'var(--r-pill)', fontWeight: 700,
              background: trade.direction === 'BUY' ? 'var(--grad-success)' : 'var(--grad-danger)',
              color: '#fff',
              boxShadow: trade.direction === 'BUY' ? '0 2px 8px rgba(34,197,94,0.25)' : '0 2px 8px rgba(239,68,68,0.25)',
              letterSpacing: '0.04em',
            }}>
              {pos.isOption ? `BUY ${trade.option_type}` : trade.direction}
            </span>
            {pos.isOption && (
              <span style={{
                fontSize: 9, padding: '3px 8px', borderRadius: 'var(--r-pill)', fontWeight: 700,
                background: 'var(--purple-dim)', color: '#c4b5fd',
                border: '1px solid rgba(168,85,247,0.25)',
              }}>
                Strike ${trade.option_strike}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Opened {new Date(trade.opened_at).toLocaleDateString()}</div>
        </div>

        {/* P&L badge */}
        <div className="pnl-badge" style={{
          background: up ? 'var(--green-dim)' : 'var(--red-dim)',
          border: `1px solid ${up ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
          borderRadius: 'var(--r-md)', padding: '10px 18px', textAlign: 'right', minWidth: 150,
          boxShadow: up ? '0 0 24px rgba(34,197,94,0.06)' : '0 0 24px rgba(239,68,68,0.06)',
        }}>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Unrealized P&L</div>
          <div className="mono" style={{ fontSize: 24, fontWeight: 800, color: up ? 'var(--green)' : 'var(--red)', letterSpacing: '-0.5px', lineHeight: 1.05 }}>
            {up ? '+' : '-'}{cs}{Math.abs(pos.unrealizedPnl).toFixed(2)}
          </div>
          <div className="mono" style={{ fontSize: 12, color: up ? 'var(--green-soft)' : 'var(--red-soft)', marginTop: 2, fontWeight: 600 }}>
            {pos.pnlPct >= 0 ? '+' : ''}{pos.pnlPct.toFixed(2)}%
          </div>
        </div>
      </div>

      {/* Stop / Entry / Target / Current price rail — same visual language as
          the Featured Signal on the Overview page. Renders only when the
          trade has both stop_price and target_price set (which is true for
          every trade opened via the new auto-close flow). The diamond
          marker shows where the live price is right now relative to the
          three reference levels. */}
      {Number.isFinite(parseFloat(trade.stop_price)) && Number.isFinite(parseFloat(trade.target_price)) && (
        <div style={{ marginBottom: 14 }}>
          <PriceRail
            entry={parseFloat(trade.price)}
            target={parseFloat(trade.target_price)}
            stop={parseFloat(trade.stop_price)}
            current={pos.currentPrice}
            cs={cs}
          />
        </div>
      )}

      {/* Option note */}
      {pos.isOption && (() => {
        // Polygon's snapshot exposes two distinct timestamps:
        //   quotedAt    — when bid/ask was last updated by a market maker.
        //                 Null when there's no live bid/ask at all.
        //   lastTradeAt — when the contract last actually traded.
        //                 Can be days/weeks old for deep-OTM names.
        // The previous version showed lastTradeAt mislabelled as "quote",
        // producing "quote 673h ago" on a contract that simply has no live
        // bid/ask. Now both are surfaced separately so the source of any
        // staleness is honest. IV is also hidden when no live quote exists
        // because Polygon's IV calc with no liquid bid/ask is garbage
        // (we've seen 900%+ on these).
        const fmtAge = (ms) => {
          if (ms == null) return null;
          const m = Math.max(0, Math.floor(ms / 60000));
          if (m < 1)   return 'just now';
          if (m < 60)  return `${m}m ago`;
          const h = Math.floor(m / 60);
          if (h < 24)  return `${h}h ${m % 60}m ago`;
          const d = Math.floor(h / 24);
          return `${d}d ${h % 24}h ago`;
        };
        const now             = Date.now();
        const quoteAgeMs      = liveOptQ?.quotedAt     != null ? now - liveOptQ.quotedAt     : null;
        const tradeAgeMs      = liveOptQ?.lastTradeAt  != null ? now - liveOptQ.lastTradeAt  : null;
        const dayAgeMs        = liveOptQ?.dayUpdatedAt != null ? now - liveOptQ.dayUpdatedAt : null;
        const hasLiveQuote    = liveOptQ?.bid != null && liveOptQ?.ask != null;
        const noUsablePrice   = pos.pricingSource === 'entry';
        const quoteIsStale    = quoteAgeMs != null && quoteAgeMs >= 15 * 60 * 1000;
        // 'session_close' = Polygon returned no last_quote (Options Starter
        // tier limitation for inactive contracts) but did return today's day
        // aggregate. The price IS real — it's the day's close — but it
        // doesn't tick. Surfacing that distinction so users don't read
        // "No live bid/ask" as "the integration is broken".
        const usingDayClose   = liveOptQ?.midpointSource === 'session_close';
        const showWarn        = noUsablePrice || quoteIsStale || (!hasLiveQuote && !usingDayClose);
        return (
        <div style={{
          fontSize: 10, marginBottom: 8, padding: '6px 10px', borderRadius: 6,
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          color: '#93c5fd',
          background: showWarn ? 'rgba(245,158,11,0.07)' : 'rgba(59,130,246,0.06)',
          border:     showWarn ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(59,130,246,0.12)',
        }}>
          <span style={{ fontWeight: 600, color: '#cbd5e1' }}>Polygon</span>
          {noUsablePrice ? (
            <span
              style={{ color: '#f59e0b', fontWeight: 600 }}
              title="No live bid/ask and the last trade is too stale to trust as a current premium. Showing entry price — your unrealised P&L will read $0 until a fresh quote arrives."
            >
              ⚠ No current premium — using entry price
            </span>
          ) : hasLiveQuote ? (() => {
            // On Polygon Options Developer the snapshot returns sizes alongside
            // bid/ask. Surface them so the user can see depth at a glance, plus
            // the spread as a % of bid — anything > 30% is flagged amber as
            // "wide spread, execution will hurt".
            const bid    = Number(liveOptQ.bid);
            const ask    = Number(liveOptQ.ask);
            const spread = bid > 0 ? ((ask - bid) / bid) * 100 : null;
            const wide   = spread != null && spread >= 30;
            const sizeStr = (sz) => (sz != null && Number.isFinite(sz)) ? ` (${sz})` : '';
            return (
              <span>
                Bid <b style={{ color: '#cbd5e1' }}>{cs}{bid.toFixed(2)}</b>{sizeStr(liveOptQ.bidSize)}
                {' / '}
                Ask <b style={{ color: '#cbd5e1' }}>{cs}{ask.toFixed(2)}</b>{sizeStr(liveOptQ.askSize)}
                {spread != null && (
                  <span
                    title={wide
                      ? `Spread is ${spread.toFixed(0)}% of bid — entering at ask and exiting at bid would cost ~${spread.toFixed(0)}% of premium in friction. Consider a more liquid contract.`
                      : `Spread is ${spread.toFixed(0)}% of bid — normal for liquid contracts.`}
                    style={{ color: wide ? '#f59e0b' : '#475569', marginLeft: 6, whiteSpace: 'nowrap', fontWeight: wide ? 600 : 400 }}
                  >
                    {wide ? '⚠ ' : ''}spread {spread.toFixed(0)}%
                  </span>
                )}
              </span>
            );
          })() : usingDayClose ? (
            <span
              title="Polygon's Options Starter plan returns day aggregates (open/high/low/close/volume) + Greeks + IV + Open Interest but does NOT include real-time bid/ask quotes for this contract. The premium shown is the latest session close — a real, accurate mark, just not a tick-by-tick quote. For real-time NBBO bid/ask you'd need Polygon Options Developer ($79/mo) or Advanced ($199/mo)."
            >
              Last close <b style={{ color: '#cbd5e1' }}>{cs}{Number(liveOptQ.midpoint).toFixed(2)}</b>
            </span>
          ) : (
            <span style={{ color: '#f59e0b' }}>⚠ No live bid/ask and no day close</span>
          )}
          {hasLiveQuote && quoteAgeMs != null && (
            <span
              title={`Polygon last updated the bid/ask ${fmtAge(quoteAgeMs)}. Illiquid contracts can go long stretches between re-quotes by market makers — the price isn't broken, the contract isn't actively quoted.`}
              style={{ color: quoteIsStale ? '#f59e0b' : '#475569', fontWeight: quoteIsStale ? 600 : 400, whiteSpace: 'nowrap' }}
            >
              {quoteIsStale ? '⚠ ' : ''}quote {fmtAge(quoteAgeMs)}
            </span>
          )}
          {usingDayClose && dayAgeMs != null && (
            <span
              title={`Polygon's day aggregate was last refreshed ${fmtAge(dayAgeMs)}. Updates intraday as new trades print across all venues.`}
              style={{ color: '#475569', whiteSpace: 'nowrap' }}
            >
              day updated {fmtAge(dayAgeMs)}
            </span>
          )}
          {tradeAgeMs != null && (
            <span
              title="When this specific contract last actually traded. Different from the bid/ask update — many OTM contracts can go days without a trade even when bid/ask are quoted."
              style={{ color: '#475569', whiteSpace: 'nowrap' }}
            >
              last trade {fmtAge(tradeAgeMs)}
            </span>
          )}
          {/* Tier indicator — surfaces Polygon's own timeframe tag so the user
              can see whether the response is REAL-TIME or DELAYED without
              opening DevTools. Only shown when we have at least one timeframe
              field populated (i.e. the upgrade did something). */}
          {(liveOptQ?.quoteTimeframe || liveOptQ?.lastTradeTimeframe) && (
            <span
              title={`Polygon's timeframe tags on this response — quote: ${liveOptQ.quoteTimeframe || '—'}, trade: ${liveOptQ.lastTradeTimeframe || '—'}. REAL-TIME = your tier serves live data. DELAYED = 15-min lagged feeds (Developer trades). Missing last_quote entirely means NBBO bid/ask is not entitled on this key — Advanced tier required.`}
              style={{
                color: (liveOptQ.quoteTimeframe === 'REAL-TIME') ? '#10b981' : '#f59e0b',
                whiteSpace: 'nowrap',
                fontSize: 9,
                fontWeight: 600,
                padding: '0 4px',
                borderRadius: 3,
                background: (liveOptQ.quoteTimeframe === 'REAL-TIME') ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
              }}
            >
              {liveOptQ.quoteTimeframe === 'REAL-TIME'
                ? 'NBBO real-time'
                : liveOptQ.quoteTimeframe === 'DELAYED'
                  ? 'NBBO delayed'
                  : liveOptQ.lastTradeTimeframe === 'DELAYED'
                    ? 'trades delayed · no NBBO'
                    : 'tier unknown'}
            </span>
          )}
          {liveOptQ?.iv != null && liveOptQ.iv < 5 && (
            <span title="Implied Volatility — the market's expectation of future price swings, annualised. High IV = expensive premiums.">
              Impl. Vol {(liveOptQ.iv * 100).toFixed(1)}%
            </span>
          )}
          {trade.option_expiry && (
            <span style={{ color: '#475569', whiteSpace: 'nowrap' }}>
              {/* formatDateOnly avoids the timezone shift that turned a 6/18
                  contract into "6/17" for users in UTC- timezones — see the
                  helper's docstring. */}
              Expires {formatDateOnly(trade.option_expiry)}
            </span>
          )}
        </div>
        );
      })()}

      {/* Detail grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          { label: pos.isOption ? 'Contracts' : 'Shares', value: pos.qty },
          { label: pos.isOption ? 'Entry Premium' : 'Entry Price', value: `${cs}${pos.entryPrice.toFixed(2)}` },
          {
            // Label honestly reflects where the number came from. "Est. Premium"
            // implied a model-derived estimate, but on Options Developer
            // without NBBO the mark is literally Polygon's day.close — so we
            // call it that. When NBBO arrives the label flips to "Mid Premium"
            // (bid+ask midpoint), and when we fall back to entry price the
            // label says "Entry Premium" verbatim so users don't think it's
            // a live mark.
            label: pos.isOption
              ? (pos.pricingSource === 'live'          ? 'Mid Premium'
               : pos.pricingSource === 'polygon_closed' ? 'Last Close'
               : pos.pricingSource === 'entry'          ? 'Entry Premium'
               :                                          'Premium')
              : 'Current Price',
            value: `${cs}${pos.currentPrice.toFixed(2)}`,
            sub: `${priceChange >= 0 ? '↑' : '↓'} ${cs}${Math.abs(priceChange).toFixed(2)} (${priceChangePct >= 0 ? '+' : ''}${priceChangePct.toFixed(1)}%)`,
            subColor: priceChange >= 0 ? '#10b981' : '#ef4444',
          },
          { label: 'Cost Basis', value: `${cs}${pos.costBasis.toFixed(2)}` },
          { label: 'Current Value', value: `${cs}${pos.currentVal.toFixed(2)}`, color: up ? '#10b981' : '#ef4444' },
          {
            label: pos.isOption ? `P&L / Contract` : `P&L / Share`,
            value: `${up ? '+' : '-'}${cs}${Math.abs(pos.isOption ? pos.unrealizedPnl / pos.qty : priceChange).toFixed(2)}`,
            color: up ? '#10b981' : '#ef4444',
          },
        ].map(item => (
          <div key={item.label} style={{ background: 'rgba(30,41,59,0.4)', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ fontSize: 10, color: '#475569' }}>{item.label}</div>
            <div className="mono" style={{ fontSize: 14, fontWeight: 600, color: item.color || '#e2e8f0' }}>{item.value}</div>
            {item.sub && <div className="mono" style={{ fontSize: 10, color: item.subColor }}>{item.sub}</div>}
          </div>
        ))}
      </div>

      {/* Signal health — tracks the underlying's conditions, not option premium.
          For options: use stock price for BB/VWAP comparisons; suppress the
          "% from entry" note (comparing stock price to option premium is meaningless). */}
      <SignalHealth
        ticker={trade.ticker}
        direction={trade.direction}
        entryPrice={pos.isOption ? 0 : trade.price}
        currentPrice={pos.isOption ? pos.stockPrice : pos.currentPrice}
        optionContext={pos.isOption ? {
          isOption:     true,
          pnlPct:       pos.pnlPct,
          optionExpiry: trade.option_expiry,
        } : null}
      />

      {/* Actions */}
      <div className="position-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => !closing && onClose(trade)} disabled={closing} style={{
          padding: '9px 20px', borderRadius: 8, border: 'none', fontFamily: 'inherit',
          background: closing
            ? 'rgba(51,65,85,0.5)'
            : up ? 'linear-gradient(135deg,#10b981,#059669)' : 'linear-gradient(135deg,#ef4444,#dc2626)',
          color: closing ? '#64748b' : '#fff', fontSize: 12, fontWeight: 600,
          cursor: closing ? 'wait' : 'pointer',
        }}>
          {closing
            ? 'Fetching price…'
            : up
              ? `Close & Take Profit (+${cs}${pos.unrealizedPnl.toFixed(2)})`
              : `Close & Cut Loss (-${cs}${Math.abs(pos.unrealizedPnl).toFixed(2)})`}
        </button>
        <button onClick={() => onViewChart(trade.ticker)} style={{
          padding: '9px 16px', borderRadius: 8, border: '1px solid rgba(99,102,241,0.3)',
          background: 'transparent', color: '#818cf8', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}>View Chart</button>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function TradingPage() {
  const { setSelectedTicker } = useOutletContext();
  const location = useLocation();
  const { config } = useExchange();
  const cs = config.currencySymbol;

  const [portfolio, setPortfolio]     = useState(null);
  const [openTrades, setOpenTrades]   = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [plHistory, setPlHistory]     = useState([]);
  const [liveQuotes, setLiveQuotes]     = useState({});
  const [optionQuotes, setOptionQuotes] = useState({}); // contractSymbol → Polygon quote
  const [loading, setLoading]         = useState(true);
  const toast = useToast();
  const [showForm, setShowForm]       = useState(false);
  const [tab, setTab]                 = useState('stocks');
  const [closingTradeId, setClosingTradeId] = useState(null);

  // Trade form
  const [formTicker,    setFormTicker]    = useState('');
  const [formDir,       setFormDir]       = useState('BUY');
  const [formQty,       setFormQty]       = useState('');
  const [formPrice,     setFormPrice]     = useState('');
  const [formType,      setFormType]      = useState('stock');
  const [formOptType,   setFormOptType]   = useState('CALL');
  const [formOptStrike, setFormOptStrike] = useState('');
  const [formOptExpiry, setFormOptExpiry] = useState('');
  const [formNotes,     setFormNotes]     = useState('');
  const [formOptContractSymbol, setFormOptContractSymbol] = useState('');
  const [formSignalSource,      setFormSignalSource]      = useState('');
  // T4.1/T4.2: stop price tracked alongside the form (hidden field, sourced
  // from the originating signal when present). Used to compute the suggested
  // qty hint and persisted on execute so portfolio-heat math knows the
  // position's true risk.
  const [formStop,    setFormStop]    = useState('');
  // Take-profit target — same UX as the stop price field. Default sourced
  // from the originating signal's targetPrice (when prefill comes from a
  // SignalCard). Persisted so the backend auto-close monitor knows what
  // level to exit on for a win. User may edit or clear to disable the
  // auto-target.
  const [formTarget,  setFormTarget]  = useState('');
  const [formSwing,   setFormSwing]   = useState('');
  // T4.2: portfolio heat snapshot — drives the top-of-page warning banner +
  // the T4.3 in-form guard. Loaded after the portfolio fetch settles so the
  // initial-render path stays fast.
  const [heat, setHeat] = useState(null);

  // Prefill from signal navigation
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    setFormTicker(prefill.ticker || '');
    setFormPrice(prefill.price?.toFixed?.(2) || prefill.price?.toString() || '');
    setFormType(prefill.type || 'stock');
    setFormNotes(prefill.notes || '');
    // T4.1/T4.2: thread stop + swing through so the persisted trade carries
    // them and the form can compute a suggested qty.
    setFormStop(prefill.stopPrice != null ? String(prefill.stopPrice) : '');
    setFormTarget(prefill.targetPrice != null ? String(prefill.targetPrice) : '');
    setFormSwing(prefill.swingDays != null ? String(prefill.swingDays) : '');
    if (prefill.type === 'option') {
      const optType = prefill.optionType || 'CALL';
      setFormOptType(optType);
      // For options, direction is derived from Call/Put, not the signal direction
      setFormDir(optType === 'CALL' ? 'BUY' : 'SHORT');
      setFormOptStrike(prefill.optionStrike?.toString() || '');
      setFormOptExpiry(prefill.optionExpiry || '');
      setFormOptContractSymbol(prefill.optionContractSymbol || '');
    } else {
      setFormDir(prefill.direction || 'BUY');
    }
    setFormSignalSource(prefill.signalSource || '');
    // T4.1: pre-fill suggested qty from the user's cash + the signal's
    // risk-per-unit. cash_balance lives on the portfolio object loaded by
    // loadAll(); we read it from the current portfolio state via a ref-style
    // pattern to avoid an effect dependency loop.
    const cash = parseFloat(portfolio?.cash_balance);
    if (Number.isFinite(cash) && cash > 0) {
      const suggestion = computeSuggestedPositionSize({
        type:         prefill.type || 'stock',
        currentPrice: parseFloat(prefill.price) || 0,
        stop:         prefill.stopPrice != null ? parseFloat(prefill.stopPrice) : null,
        option:       prefill.type === 'option' ? { premium: parseFloat(prefill.price) || 0 } : null,
      }, cash);
      setFormQty(suggestion?.qty ? String(suggestion.qty) : '');
    } else {
      setFormQty('');
    }
    setShowForm(true);
    setTab(prefill.type === 'option' ? 'options' : 'stocks');
    window.history.replaceState({}, '');
  // We intentionally omit `portfolio` from deps so a portfolio refresh after
  // mount doesn't blow away a user-edited qty. Prefill only runs on signal
  // navigation (location.state changes).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t, h] = await Promise.all([
        apiGetPortfolio(),
        apiGetTrades(),
        apiGetPortfolioHistory(30),
      ]);
      setPortfolio(p.portfolio);
      setOpenTrades(p.openTrades || []);
      setTradeHistory(t.trades || []);
      setPlHistory(h.history || []);

      // T4.2: heat fetched off the critical path. apiGetPortfolioHeat is
      // defensive (returns null on any error) so this never blocks rendering.
      apiGetPortfolioHeat().then(h => setHeat(h));

      // Fetch real Polygon prices for all open option trades.
      // Trades entered from signal cards have option_contract_symbol stored → single-contract endpoint.
      // Older / manually entered trades don't → fall back to chain lookup by type+strike+expiry.
      const openTrades = p.openTrades || [];
      const optQ = {};
      await Promise.all(
        openTrades
          .filter(t => t.asset_type === 'option')
          .map(async (trade) => {
            let quote = null;
            if (trade.option_contract_symbol) {
              quote = await getOptionContractQuote(trade.ticker, trade.option_contract_symbol);
            }
            if (!quote && trade.option_type && trade.option_strike && trade.option_expiry) {
              quote = await getOptionQuoteByDetails(
                trade.ticker, trade.option_type,
                parseFloat(trade.option_strike), trade.option_expiry
              );
            }
            if (quote) optQ[trade.id] = quote;
          })
      );
      setOptionQuotes(optQ);

      const tickers = [...new Set(openTrades.map(t => t.ticker))];
      setLiveQuotes(tickers.length ? await getBatchQuotes(tickers) : {});
    } catch (e) { console.error('Load error:', e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (openTrades.length === 0) return;
    const iv = setInterval(async () => {
      const tickers = [...new Set(openTrades.map(t => t.ticker))];
      if (tickers.length) setLiveQuotes(await getBatchQuotes(tickers));

      // Refresh real option prices
      const optQ = {};
      await Promise.all(
        openTrades
          .filter(t => t.asset_type === 'option')
          .map(async (t) => {
            let quote = null;
            if (t.option_contract_symbol) {
              quote = await getOptionContractQuote(t.ticker, t.option_contract_symbol);
            }
            if (!quote && t.option_type && t.option_strike && t.option_expiry) {
              quote = await getOptionQuoteByDetails(
                t.ticker, t.option_type,
                parseFloat(t.option_strike), t.option_expiry
              );
            }
            if (quote) optQ[t.id] = quote;
          })
      );
      setOptionQuotes(optQ);
    }, 30000);
    return () => clearInterval(iv);
  }, [openTrades]);

  // ── Portfolio totals ─────────────────────────────────────────────────────
  // totalPositionValue = sum of "what you'd get back if all positions closed now"
  // For SHORT this is costBasis + unrealizedPnl (not just currentVal)
  // IMPORTANT: must pass optionQuotes so options are priced with live Polygon data,
  // falling back to entry price — avoids phantom P&L from stale estimates.
  const totalPositionValue = openTrades.reduce((sum, trade) => {
    const pos = calcPosition(trade, liveQuotes[trade.ticker], optionQuotes[trade.id] ?? null);
    return sum + pos.portfolioValue;
  }, 0);

  const cashBalance    = parseFloat(portfolio?.cash_balance || 0);
  const totalValue     = cashBalance + totalPositionValue;
  const initialBalance = parseFloat(portfolio?.initial_balance || 10000);
  const totalPnl       = totalValue - initialBalance;
  const totalPnlPct    = initialBalance > 0 ? (totalPnl / initialBalance) * 100 : 0;

  // ── Split trades into stock / options ────────────────────────────────────
  const stockTrades  = openTrades.filter(t => t.asset_type !== 'option');
  const optionTrades = openTrades.filter(t => t.asset_type === 'option');

  // ── Actions ──────────────────────────────────────────────────────────────
  const executeTrade = async () => {
    if (!formTicker || !formQty || !formPrice) { toast.error('Fill all required fields'); return; }
    try {
      const result = await apiExecuteTrade({
        ticker: formTicker.toUpperCase(), direction: formDir,
        assetType: formType, quantity: parseFloat(formQty), price: parseFloat(formPrice),
        optionType:           formType === 'option' ? formOptType   : null,
        optionStrike:         formType === 'option' ? parseFloat(formOptStrike) : null,
        optionExpiry:         formType === 'option' ? formOptExpiry : null,
        optionContractSymbol: formType === 'option' ? (formOptContractSymbol || null) : null,
        signalSource: formSignalSource || 'Manual',
        notes: formNotes,
        // T4.1/T4.2: persist the originating signal's stop + swing for the
        // portfolio-heat math and the max-hold sweep. Both optional — manual
        // trades work as before (server falls back to 2%-of-entry risk + 5d
        // swing).
        stopPrice:   formStop   ? parseFloat(formStop)   : null,
        targetPrice: formTarget ? parseFloat(formTarget) : null,
        swingDays:   formSwing  ? parseInt(formSwing, 10) : null,
      });
      toast.success(result.message);
      setShowForm(false);
      loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const closePosition = async (trade) => {
    if (closingTradeId) return; // prevent double-close
    setClosingTradeId(trade.id);
    try {
      let closePrice;
      if (trade.asset_type === 'option') {
        let freshOptQ = null;
        if (trade.option_contract_symbol) {
          freshOptQ = await getOptionContractQuote(trade.ticker, trade.option_contract_symbol, { force: true });
        }
        if (!freshOptQ && trade.option_type && trade.option_strike && trade.option_expiry) {
          freshOptQ = await getOptionQuoteByDetails(
            trade.ticker, trade.option_type,
            parseFloat(trade.option_strike), trade.option_expiry,
            { force: true }
          );
        }
        if (freshOptQ?.midpoint != null) {
          closePrice = freshOptQ.midpoint;
          setOptionQuotes(prev => ({ ...prev, [trade.id]: freshOptQ }));
        } else {
          closePrice = parseFloat(trade.price);
        }
      } else {
        const freshQ = await getQuote(trade.ticker, { bypassCache: true });
        if (freshQ?.price > 0) {
          closePrice = freshQ.price;
          setLiveQuotes(prev => ({ ...prev, [trade.ticker]: freshQ }));
        } else {
          closePrice = parseFloat(trade.price);
        }
      }
      const result = await apiCloseTrade(trade.id, closePrice);
      toast.success(result.message);
      loadAll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setClosingTradeId(null);
    }
  };

  const openNewTrade = () => {
    setFormTicker(''); setFormDir('BUY'); setFormQty(''); setFormPrice('');
    setFormType('stock'); setFormOptType('CALL'); setFormOptStrike(''); setFormOptExpiry('');
    setFormOptContractSymbol(''); setFormSignalSource(''); setFormNotes('');
    setFormStop(''); setFormTarget(''); setFormSwing(''); setShowForm(true);
  };

  // T4.1 / T4.3: projected risk + heat for the open Trade form. Computed off
  // the live form fields so the suggested-qty hint and the over-heat warning
  // update as the user types. Returns null when fields aren't filled enough
  // to do the math.
  const formRiskProjection = (() => {
    const qty   = parseFloat(formQty);
    const entry = parseFloat(formPrice);
    if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(entry) || entry <= 0) return null;
    const cash = parseFloat(portfolio?.cash_balance);
    if (!Number.isFinite(cash) || cash <= 0) return null;

    // Per-trade risk dollars
    let tradeRisk;
    let stopBasis = 'fallback-2pct';
    if (formType === 'option') {
      tradeRisk = qty * entry * 100 * 0.5;
      stopBasis = 'option-50pct-drawdown';
    } else {
      const stop = parseFloat(formStop);
      if (Number.isFinite(stop) && stop > 0) {
        tradeRisk = qty * Math.abs(entry - stop);
        stopBasis = 'entry-minus-stop';
      } else {
        tradeRisk = qty * entry * 0.02;   // matches server STOP_FALLBACK_PCT
      }
    }
    const equity      = cash + (heat?.positionsValue || 0);
    const currentHeat = heat?.totalHeatDollars || 0;
    const projHeatPct = equity > 0 ? ((currentHeat + tradeRisk) / equity) * 100 : 0;
    const tradeRiskPct = equity > 0 ? (tradeRisk / equity) * 100 : 0;

    // Suggested qty for the same setup — shown alongside the entered qty so
    // the user can compare "what would risk 0.75% of equity" vs "what I'm
    // entering".
    const suggestion = computeSuggestedPositionSize({
      type:         formType,
      currentPrice: entry,
      stop:         Number.isFinite(parseFloat(formStop)) ? parseFloat(formStop) : null,
      option:       formType === 'option' ? { premium: entry } : null,
    }, cash);

    const heatLimit = heat?.thresholds?.heatWarnPct ?? 6;
    return {
      tradeRisk:    +tradeRisk.toFixed(2),
      tradeRiskPct: +tradeRiskPct.toFixed(2),
      projHeatPct:  +projHeatPct.toFixed(2),
      heatLimit,
      overHeat:     projHeatPct > heatLimit,
      overTradeRisk: tradeRiskPct > 1.5,   // ≈ 2× the recommended 0.75% per trade
      suggestedQty: suggestion?.qty ?? null,
      stopBasis,
    };
  })();

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 13,
    border: '1px solid rgba(51,65,85,0.5)', background: 'rgba(15,23,42,0.6)',
    color: '#e2e8f0', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 'var(--r-md)',
              background: 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(34,197,94,0.06))',
              border: '1px solid rgba(34,197,94,0.25)',
            }}>
              <DollarSign size={18} color="var(--green)" strokeWidth={2.4} />
            </span>
            Paper Trading
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 0 0 0' }}>
            {config.flag} {config.label} · Practice with {cs}{initialBalance.toLocaleString()} mock money
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="primary" size="md" icon={Plus} onClick={openNewTrade}>
            New Trade
          </Button>
          <Button variant="ghost" size="md" icon={RefreshCw} onClick={loadAll}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Portfolio summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
        {loading
          ? Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="card" style={{ padding: '14px 16px' }}>
                <div className="skeleton" style={{ height: 10, width: 80, borderRadius: 3, marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 22, width: '70%', borderRadius: 4, marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 10, width: 60, borderRadius: 3 }} />
              </div>
            ))
          : [
              { label: 'Total Value',    raw: totalValue,         prefix: cs,                 sign: '', decimals: 2, color: 'var(--text-primary)', icon: Wallet,    tint: 'rgba(91,106,240,0.05)',  border: 'rgba(91,106,240,0.20)',  sub: 'Cash + Positions', tip: TERMS.totalValue },
              { label: 'Cash Available', raw: cashBalance,        prefix: cs,                 sign: '', decimals: 2, color: '#38bdf8',             icon: DollarSign,tint: 'rgba(14,165,233,0.06)',  border: 'rgba(14,165,233,0.20)',  sub: 'Ready to trade',   tip: TERMS.cash },
              { label: 'Positions',      raw: totalPositionValue, prefix: cs,                 sign: '', decimals: 2, color: '#c4b5fd',             icon: TrendingUp,tint: 'var(--purple-dim)',      border: 'rgba(168,85,247,0.20)',  sub: `${stockTrades.length} stock · ${optionTrades.length} option`, tip: 'Live mark-to-market value of every open position. For LONG = qty × current price; for SHORT = committed capital + unrealised P&L; for options = qty × premium × 100.' },
              { label: 'Total P&L',      raw: Math.abs(totalPnl), prefix: `${totalPnl >= 0 ? '+' : '-'}${cs}`, sign: '', decimals: 2, color: totalPnl >= 0 ? 'var(--green)' : 'var(--red)', icon: null, tint: totalPnl >= 0 ? 'var(--green-dim)' : 'var(--red-dim)', border: totalPnl >= 0 ? 'rgba(34,197,94,0.20)' : 'rgba(239,68,68,0.20)', sub: `${totalPnlPct >= 0 ? '+' : ''}${totalPnlPct.toFixed(1)}%`, tip: 'Realised + unrealised P&L vs. the starting balance. The % is annualised-style: pure (current − starting) / starting, not time-weighted.' },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={c.label} title={c.tip} className="portfolio-card animate-fade-in stagger" style={{
                  background: c.tint, borderRadius: 'var(--r-md)', padding: '14px 16px',
                  border: `1px solid ${c.border}`, overflow: 'hidden',
                  '--i': i, cursor: 'help',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</span>
                    {Icon && <Icon size={12} color={c.color} strokeWidth={2} />}
                  </div>
                  <div className="mono" style={{ fontSize: 22, fontWeight: 800, color: c.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.4px', lineHeight: 1.05 }}>
                    <CountUp end={c.raw} prefix={c.prefix} decimals={c.decimals} duration={900} />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>{c.sub}</div>
                </div>
              );
            })
        }
      </div>

      {/* T4.2: portfolio heat banner — fires when total open risk > 6% of
          equity OR any sector > 25% of mark-to-market positions. Advisory
          only (we never block trades), but it surfaces the failure mode
          that turns a 60% WR strategy into a losing one. */}
      {heat?.warnings && heat.warnings.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '10px 14px', marginBottom: 16, borderRadius: 10,
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.30)',
        }}>
          <AlertTriangle size={16} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 12, color: '#fbbf24', lineHeight: 1.5 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Portfolio risk warning
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8 }}>
                · open risk {heat.totalHeatPct.toFixed(1)}% of equity
              </span>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {heat.warnings.map((w, i) => <li key={i}>{w.message}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* P&L chart */}
      {plHistory.length > 1 && (
        <div style={{ background: 'rgba(15,23,42,0.6)', borderRadius: 14, padding: '16px 18px', border: '1px solid rgba(51,65,85,0.3)', marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600, marginBottom: 8 }}>Portfolio Value Over Time</div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={plHistory.map(h => ({ date: new Date(h.recorded_at).toLocaleDateString(), value: parseFloat(h.total_value) }))}>
              <defs>
                <linearGradient id="gPnl" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={totalPnl >= 0 ? '#10b981' : '#ef4444'} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={totalPnl >= 0 ? '#10b981' : '#ef4444'} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.3)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#475569' }} />
              <YAxis tick={{ fontSize: 10, fill: '#475569' }} domain={['auto', 'auto']} tickFormatter={v => `${cs}${v.toFixed(0)}`} />
              <Tooltip contentStyle={tooltipStyle} formatter={v => [`${cs}${parseFloat(v).toFixed(2)}`, 'Value']} />
              <Area type="monotone" dataKey="value" stroke={totalPnl >= 0 ? '#10b981' : '#ef4444'} fill="url(#gPnl)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: 'stocks',  label: 'Stocks',  count: stockTrades.length },
          { key: 'options', label: 'Options', count: optionTrades.length },
          { key: 'history', label: 'History', count: null },
        ].map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              background: active ? 'var(--grad-accent)' : 'transparent',
              color:      active ? '#fff'               : 'var(--text-secondary)',
              border:     active ? 'none'               : '1px solid var(--border)',
              boxShadow:  active ? '0 4px 14px rgba(91,106,240,0.30)' : 'none',
              transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)',
            }}>
              {t.label}
              {t.count != null && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 18, height: 18, padding: '0 5px', borderRadius: 'var(--r-pill)',
                  fontSize: 10, fontWeight: 700,
                  background: active ? 'rgba(255,255,255,0.18)' : 'var(--bg-elevated)',
                  color: active ? '#fff' : 'var(--text-muted)',
                }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Stocks tab ── */}
      {tab === 'stocks' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading && (
            Array.from({ length: 2 }, (_, i) => (
              <div key={i} style={{ background: 'rgba(15,23,42,0.6)', borderRadius: 14, padding: '18px 20px', border: '1px solid rgba(51,65,85,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div>
                    <div className="skeleton" style={{ height: 18, width: 80, borderRadius: 4, marginBottom: 6 }} />
                    <div className="skeleton" style={{ height: 10, width: 120, borderRadius: 3 }} />
                  </div>
                  <div className="skeleton" style={{ height: 56, width: 140, borderRadius: 10 }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                  {Array.from({ length: 3 }, (_, j) => (
                    <div key={j}>
                      <div className="skeleton" style={{ height: 10, width: 60, borderRadius: 3, marginBottom: 4 }} />
                      <div className="skeleton" style={{ height: 14, width: 80, borderRadius: 3 }} />
                    </div>
                  ))}
                </div>
                <div className="skeleton" style={{ height: 34, width: '100%', borderRadius: 8 }} />
              </div>
            ))
          )}
          {!loading && stockTrades.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: '#475569' }}>
              <DollarSign size={40} style={{ opacity: 0.2, marginBottom: 8 }} />
              <div style={{ fontSize: 15, fontWeight: 600 }}>No open stock positions</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Use signals or New Trade to get started</div>
            </div>
          )}
          {stockTrades.map(trade => (
            <PositionCard key={trade.id} trade={trade} liveQuotes={liveQuotes} optionQuotes={optionQuotes} cs={cs}
              onClose={closePosition} onViewChart={setSelectedTicker} closing={closingTradeId === trade.id} />
          ))}
        </div>
      )}

      {/* ── Options tab ── */}
      {tab === 'options' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!loading && optionTrades.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: '#475569' }}>
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }}>⬡</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>No open options positions</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Execute a trade with type "option" to see it here</div>
            </div>
          )}
          {optionTrades.map(trade => (
            <PositionCard key={trade.id} trade={trade} liveQuotes={liveQuotes} optionQuotes={optionQuotes} cs={cs}
              onClose={closePosition} onViewChart={setSelectedTicker} closing={closingTradeId === trade.id} />
          ))}
        </div>
      )}

      {/* ── History tab ── */}
      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tradeHistory.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#475569' }}>No trades yet</div>}
          {tradeHistory.map(trade => {
            const isOpt = trade.asset_type === 'option';
            const pnl   = trade.realized_pnl != null ? parseFloat(trade.realized_pnl) : null;
            return (
              <div key={trade.id} style={{
                background: 'rgba(15,23,42,0.4)', borderRadius: 10, padding: '10px 16px',
                border: '1px solid rgba(51,65,85,0.2)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="mono" style={{ fontWeight: 700, color: '#e2e8f0' }}>{displayTicker(trade.ticker)}</span>
                  <span style={{ color: trade.direction === 'BUY' ? '#10b981' : '#ef4444', fontWeight: 600, fontSize: 10 }}>{trade.direction}</span>
                  {isOpt && <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}>{trade.option_type} ${trade.option_strike}</span>}
                  <span style={{ color: '#475569' }}>{parseFloat(trade.quantity)} @ {cs}{parseFloat(trade.price).toFixed(2)}{isOpt ? '/contract' : ''}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: trade.status === 'open' ? '#f59e0b' : '#64748b', fontSize: 10, fontWeight: 600, textTransform: 'uppercase' }}>{trade.status}</span>
                  {trade.status === 'closed' && pnl != null && (
                    <span className="mono" style={{ color: pnl >= 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                      {pnl >= 0 ? '+' : '-'}{cs}{Math.abs(pnl).toFixed(2)}
                    </span>
                  )}
                  <span style={{ color: '#475569', fontSize: 10 }}>{new Date(trade.opened_at).toLocaleDateString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Trade form modal ── */}
      {showForm && (() => {
        const totalCost = parseFloat(formQty || 0) * parseFloat(formPrice || 0) * (formType === 'option' ? 100 : 1);
        const tradeVariant = formType === 'option'
          ? (formOptType === 'CALL' ? 'long' : 'short')
          : (formDir === 'BUY' ? 'long' : 'short');
        const tradeLabel = formType === 'option'
          ? `Buy ${formOptType} — ${cs}${totalCost.toFixed(2)}`
          : `${formDir === 'BUY' ? '↑ Execute Buy' : '↓ Execute Short'} — ${cs}${totalCost.toFixed(2)}`;
        return (
        <div className="animate-fade-in" style={{
          position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
          background: 'var(--bg-overlay)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        }} onClick={() => setShowForm(false)}>
          <div className="animate-slide-up glass" style={{
            margin: 'auto', width: '95%', maxWidth: 500,
            borderRadius: 'var(--r-xl)',
            border: '1px solid rgba(91,106,240,0.20)',
            padding: '28px 28px 24px',
            boxShadow: 'var(--shadow-modal)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22, gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: 'var(--r-md)',
                  background: 'linear-gradient(135deg, rgba(91,106,240,0.20), rgba(91,106,240,0.06))',
                  border: '1px solid rgba(91,106,240,0.30)',
                }}>
                  <DollarSign size={16} color="var(--accent-soft)" strokeWidth={2.4} />
                </span>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.2px' }}>Execute Trade</h3>
              </div>
              <Button variant="ghost" size="sm" icon={X} onClick={() => setShowForm(false)} aria-label="Close trade form" />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Ticker + Direction */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Input
                  label="Ticker"
                  value={formTicker}
                  onChange={e => setFormTicker(e.target.value.toUpperCase())}
                  placeholder="AAPL"
                  size="md"
                />
                {formType === 'option' ? (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Action</div>
                    <div style={{
                      padding: '10px 14px', borderRadius: 'var(--r-md)', fontSize: 13, fontWeight: 700,
                      background: formOptType === 'CALL' ? 'var(--green-dim)' : 'var(--purple-dim)',
                      color:      formOptType === 'CALL' ? 'var(--green)'    : '#c4b5fd',
                      border: `1px solid ${formOptType === 'CALL' ? 'rgba(34,197,94,0.30)' : 'rgba(168,85,247,0.30)'}`,
                      textAlign: 'center',
                    }}>
                      Buy {formOptType}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Direction</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['BUY', 'SHORT'].map(d => {
                        const active = formDir === d;
                        return (
                          <button key={d} type="button" onClick={() => setFormDir(d)} style={{
                            flex: 1, padding: '9px 0', borderRadius: 'var(--r-md)', cursor: 'pointer',
                            fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                            background: active ? (d === 'BUY' ? 'var(--grad-success)' : 'var(--grad-danger)') : 'transparent',
                            color:      active ? '#fff' : 'var(--text-secondary)',
                            border: active ? 'none' : '1px solid var(--border)',
                            boxShadow: active ? (d === 'BUY' ? '0 4px 12px rgba(34,197,94,0.25)' : '0 4px 12px rgba(239,68,68,0.25)') : 'none',
                            transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)',
                          }}>{d === 'BUY' ? '↑ BUY' : '↓ SHORT'}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Qty + Price */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Input
                  label={formType === 'option' ? 'Contracts' : 'Shares'}
                  value={formQty}
                  onChange={e => setFormQty(e.target.value)}
                  placeholder="1"
                  type="number"
                  size="md"
                />
                <Input
                  label={formType === 'option' ? 'Premium / share ($)' : 'Price ($)'}
                  value={formPrice}
                  onChange={e => setFormPrice(e.target.value)}
                  placeholder="5.00"
                  type="number"
                  step="0.01"
                  size="md"
                  onFocus={async () => { if (formTicker && !formPrice) { try { const q = await getQuote(formTicker); setFormPrice(q.price.toFixed(2)); } catch {} } }}
                />
              </div>

              {/* Take-profit + stop-loss — defaults from signal when present.
                  Server's autoCloseMonitor closes the position the moment
                  the price hits either level. Leave blank to disable
                  auto-close for that side. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Input
                  label="Take profit ($)"
                  value={formTarget}
                  onChange={e => setFormTarget(e.target.value)}
                  placeholder="—"
                  type="number"
                  step="0.01"
                  size="md"
                  hint={formTarget ? 'Auto-closes when price hits this level' : 'Leave blank to disable target auto-close'}
                />
                <Input
                  label="Stop loss ($)"
                  value={formStop}
                  onChange={e => setFormStop(e.target.value)}
                  placeholder="—"
                  type="number"
                  step="0.01"
                  size="md"
                  hint={formStop ? 'Auto-closes when price hits this level' : 'Leave blank to disable stop auto-close'}
                />
              </div>

              {/* Asset Type */}
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Asset Type</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['stock', 'option'].map(t => {
                    const active = formType === t;
                    return (
                      <button key={t} type="button" onClick={() => setFormType(t)} style={{
                        flex: 1, padding: '9px 0', borderRadius: 'var(--r-md)', cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, fontFamily: 'inherit', textTransform: 'capitalize',
                        background: active ? 'var(--grad-accent)' : 'transparent',
                        color:      active ? '#fff' : 'var(--text-secondary)',
                        border: active ? 'none' : '1px solid var(--border)',
                        boxShadow: active ? '0 4px 12px rgba(91,106,240,0.25)' : 'none',
                        transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)',
                      }}>{t}</button>
                    );
                  })}
                </div>
              </div>

              {/* Option fields */}
              {formType === 'option' && (
                <div className="animate-fade-in" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Call / Put</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['CALL', 'PUT'].map(t => {
                        const active = formOptType === t;
                        return (
                          <button
                            key={t} type="button"
                            onClick={() => { setFormOptType(t); setFormDir(t === 'CALL' ? 'BUY' : 'SHORT'); }}
                            style={{
                              flex: 1, padding: '8px 0', borderRadius: 'var(--r-sm)', cursor: 'pointer',
                              fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
                              background: active ? (t === 'CALL' ? 'var(--grad-success)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)') : 'transparent',
                              color:      active ? '#fff' : 'var(--text-secondary)',
                              border: active ? 'none' : '1px solid var(--border)',
                              boxShadow: active ? (t === 'CALL' ? '0 3px 10px rgba(34,197,94,0.20)' : '0 3px 10px rgba(139,92,246,0.20)') : 'none',
                              transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)',
                            }}>{t}</button>
                        );
                      })}
                    </div>
                  </div>
                  <Input
                    label="Strike"
                    value={formOptStrike}
                    onChange={e => setFormOptStrike(e.target.value)}
                    type="number"
                    step="0.5"
                    size="md"
                  />
                  <Input
                    label="Expiry"
                    value={formOptExpiry}
                    onChange={e => setFormOptExpiry(e.target.value)}
                    type="date"
                    size="md"
                  />
                </div>
              )}

              {/* Cost summary */}
              <div style={{
                background: 'var(--bg-elevated)', borderRadius: 'var(--r-md)',
                padding: '12px 14px', border: '1px solid var(--border-subtle)',
              }}>
                {formType === 'option' && (
                  <div style={{ fontSize: 10, color: '#c4b5fd', marginBottom: 8, letterSpacing: '0.02em' }}>
                    {formQty || 0} contract{formQty !== '1' ? 's' : ''} × ${formPrice || 0} premium × 100 shares/contract
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Total Cost</span>
                  <span className="mono" style={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 18, letterSpacing: '-0.3px' }}>
                    {cs}{totalCost.toFixed(2)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Cash Available</span>
                  <span className="mono" style={{ color: '#38bdf8', fontWeight: 600 }}>{cs}{cashBalance.toFixed(2)}</span>
                </div>
                {/* T4.1: per-trade risk + suggested-qty hint. Always informational
                    when the form is fillable; never blocks. */}
                {formRiskProjection && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                      <span style={{ color: 'var(--text-muted)' }}>Risk if stopped out</span>
                      <span className="mono" style={{ color: formRiskProjection.overTradeRisk ? '#f59e0b' : 'var(--text-primary)', fontWeight: 600 }}>
                        {cs}{formRiskProjection.tradeRisk.toFixed(2)} ({formRiskProjection.tradeRiskPct.toFixed(2)}% of equity)
                      </span>
                    </div>
                    {formRiskProjection.suggestedQty != null && formRiskProjection.suggestedQty !== parseFloat(formQty) && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                        <span style={{ color: 'var(--text-muted)' }}>
                          Suggested ({(RISK_PER_TRADE_PCT * 100).toFixed(2)}% risk)
                        </span>
                        <button
                          onClick={() => setFormQty(String(formRiskProjection.suggestedQty))}
                          style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            background: 'none', border: 0, padding: 0, cursor: 'pointer',
                            color: 'var(--accent-soft)', fontWeight: 600, textDecoration: 'underline dotted',
                          }}
                          title={`Click to size at ${formRiskProjection.suggestedQty}`}
                        >
                          {formRiskProjection.suggestedQty} {formType === 'option' ? 'contracts' : 'shares'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* T4.3: portfolio-heat guard. Non-blocking — surfaces when the
                  PROJECTED total open risk (current + this trade) crosses the
                  6% threshold, or when this trade alone is oversized vs the
                  0.75% per-trade target. User can still execute; this just
                  makes the silent failure mode visible. */}
              {formRiskProjection && (formRiskProjection.overHeat || formRiskProjection.overTradeRisk) && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '8px 12px', borderRadius: 8, marginBottom: 10,
                  background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)',
                }}>
                  <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
                  <div style={{ fontSize: 11, color: '#fbbf24', lineHeight: 1.5 }}>
                    {formRiskProjection.overHeat && (
                      <div>
                        This trade pushes open risk to{' '}
                        <strong>{formRiskProjection.projHeatPct.toFixed(1)}%</strong> of equity (limit {formRiskProjection.heatLimit}%).
                      </div>
                    )}
                    {formRiskProjection.overTradeRisk && (
                      <div>
                        Single-trade risk is{' '}
                        <strong>{formRiskProjection.tradeRiskPct.toFixed(2)}%</strong> of equity — above the {(RISK_PER_TRADE_PCT * 100).toFixed(2)}% target. Use the suggested qty above to size to the target.
                      </div>
                    )}
                    <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>Advisory only — your trade will still execute if you proceed.</div>
                  </div>
                </div>
              )}

              <Button
                variant={tradeVariant}
                size="lg"
                fullWidth
                onClick={executeTrade}
              >
                {tradeLabel}
              </Button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
}
